import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { JSX } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import axios from "axios";
import classNames from "classnames";
import { getAuth, signOut } from "firebase/auth";
import { useSendbirdUnreadCount } from "../Layout/SendbirdUnreadContext";
import { Menu, MenuProps, Grid, Message } from "@arco-design/web-react";
import {
  IconHome,
  IconCalendar,
  IconSettings,
  IconUser,
  IconPoweroff,
  IconMenu,
  IconSearch,
  IconMessage,
  IconNotification,
  IconQrcode,
  IconStar,
} from "@arco-design/web-react/icon";

import { Logo } from "../components/Logo";
import { QrScannerModal } from "../components/QrScanner/QrScannerModal";
import { LanguageSelect } from '../components/LanguageSelect/LanguageSelect';
import { MenuNavLink } from '../components/MenuNavLink/MenuNavLink';
import { routes } from "../routes/routes";
import { RootState } from "../store/store";
import { clearUser } from "../store/userSlice";
import { getAuthLogoType, getCurrentTheme, getCurrentThemeName } from "../theme";
import { endpoints } from "../api/endpoints";
import { useAiMatchingPreview } from "../hooks/useAiMatchingPreview";
import { useEventIntakeEnabled } from "../hooks/useEventIntakeEnabled";


const Row = Grid.Row;
const Col = Grid.Col;

const moderationSeenStorageKey = () =>
  `adminModerationLastSeenAt:${window.location.hostname}`;

const MODERATION_INDICATOR_TYPES = new Set(["report", "block"]);

export const BaseMenu = (
  props: JSX.IntrinsicAttributes & MenuProps & React.RefAttributes<unknown>
) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const eventIntakeEnabled = useEventIntakeEnabled();

  const [collapse, setCollapse] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [hasStatisticsAttention, setHasStatisticsAttention] = useState(false);
  const user = useSelector((state: RootState) => state.user.user);
  const qrProfileEnabled = useSelector((state: RootState) => state.config.qrProfileEnabled);
  const isCalendlyLinkRequired = useSelector((state: RootState) => state.config.isCalendlyLinkRequired);
  const defaultPage = user?.isAdmin ? routes.ADMIN_DASHBOARD : routes.DASHBOARD;

  const aiMatchingPreview = useAiMatchingPreview();
  const usePreviewNav = aiMatchingPreview && !user?.isAdmin;
  const useLegacyAdminNav = Boolean(user?.isAdmin && !aiMatchingPreview);
  const unreadMessagesCount = useSendbirdUnreadCount();

  const dispatch = useDispatch();
  const logOut = () => {
    const auth = getAuth();

    signOut(auth)
      .then(() => {
        dispatch(clearUser());
        Message.success({content: t('notification_messages.logged_out_success'), closable: true, duration: 3000});

        setTimeout(() => {
          navigate(routes.LOGIN);
        }, 10);
      })
      .catch((error) => {
        console.error("Logout failed:", error);
        Message.error(t('notification_messages.logged_out_error'));
      });
  };
  
  useEffect(() => {
    if (!user?.isAdmin) {
      setHasStatisticsAttention(false);
      return;
    }

    const seenKey = moderationSeenStorageKey();

    // Opening Statistics marks this domain's moderation activity as seen.
    if (location.pathname === routes.ADMIN_STATISTICS) {
      localStorage.setItem(seenKey, new Date().toISOString());
      setHasStatisticsAttention(false);
      return;
    }

    let isMounted = true;

    const checkStatisticsAttention = async () => {
      try {
        let lastSeen = localStorage.getItem(seenKey);
        // First visit on this domain: don't light up for historical moderation.
        if (!lastSeen) {
          lastSeen = new Date().toISOString();
          localStorage.setItem(seenKey, lastSeen);
        }
        const lastSeenAt = new Date(lastSeen).getTime();

        const actionsRes = await axios.get(endpoints.admin.getUserActions);

        const rows = Array.isArray(actionsRes.data) ? actionsRes.data : [];
        const hasUnreadModeration = rows.some(
          (row: { created_at?: string; action_type?: string }) => {
            if (!row.created_at || !MODERATION_INDICATOR_TYPES.has(row.action_type || "")) {
              return false;
            }
            const createdAt = new Date(row.created_at).getTime();
            return Number.isFinite(createdAt) && createdAt > lastSeenAt;
          }
        );

        if (isMounted) {
          setHasStatisticsAttention(hasUnreadModeration);
        }
      } catch {
        if (isMounted) {
          setHasStatisticsAttention(false);
        }
      }
    };

    checkStatisticsAttention();
    const intervalId = setInterval(checkStatisticsAttention, 30000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [location.pathname, user?.isAdmin]);

  const menuItems = (
    <>
      {user?.isAdmin && aiMatchingPreview && (
        <MenuNavLink
          label={t("navigation_menu.upcoming_meetings")}
          to={routes.ADMIN_DASHBOARD}
          icon={IconHome}
        />
      )}

      {!user?.isAdmin && (
        <MenuNavLink
          label={t(
            aiMatchingPreview
              ? "navigation_menu.upcoming_meetings"
              : "navigation_menu.home"
          )}
          to={routes.DASHBOARD}
          icon={IconHome}
        />
      )}

      {!user?.isAdmin && (
        <MenuNavLink
          label={t(
            eventIntakeEnabled
              ? "navigation_menu.browse_people"
              : "navigation_menu.find_mentors",
          )}
          to={routes.SEARCH}
          icon={IconSearch}
        />
      )}

      {!user?.isAdmin && aiMatchingPreview && (
        <MenuNavLink
          label={t('navigation_menu.ai_intake')}
          to={routes.INTAKE}
          icon={IconStar}
        />
      )}

      {user?.isMentor && !user?.isAdmin && !isCalendlyLinkRequired && !usePreviewNav && (
        <MenuNavLink label={t('navigation_menu.set_availability')} to={routes.MENTORSETTINGS} icon={IconCalendar} />
      )}

      {user?.isMentor && !user?.isAdmin && user?.id && (
        <MenuNavLink
          label={t('navigation_menu.profile')}
          to={routes.MENTOR.replace(":id", user.id.toString())}
          icon={IconUser}
        />
      )}

      {user && !user.isMentor && !user.isAdmin && !user.isGuest && user.id && (
        <MenuNavLink
          label={t('navigation_menu.profile')}
          to={routes.MENTEE.replace(":id", user.id.toString())}
          icon={IconUser}
        />
      )}

      {user?.id && (!usePreviewNav || user.isGuest) && (
        <MenuNavLink
          label={t('navigation_menu.edit_profile')}
          to={eventIntakeEnabled && user.isGuest ? routes.GUEST_INTAKE : routes.profile}
          icon={IconSettings}
        />
      )}

      {useLegacyAdminNav && (
        <MenuNavLink label={t('navigation_menu.home')} to={routes.DASHBOARD} icon={IconHome} />
      )}

      {user?.isAdmin && aiMatchingPreview && (
        <>
          <MenuNavLink label={t('navigation_menu.home')} to={routes.DASHBOARD} icon={IconHome} />
          <MenuNavLink label={t('navigation_menu.users_list')} to={routes.ADMIN_USERS} />
          <MenuNavLink label={t('navigation_menu.create_user')} to={routes.ADMIN_USER_CREATE} />
          <MenuNavLink label={t('navigation_menu.meetings')} to={routes.ADMIN_MEETINGS} />
        </>
      )}

      {user?.isAdmin && (
        <MenuNavLink
          to={routes.ADMIN_DASHBOARD}
          menuItemChildren={(
            <div
              style={{
                position: "relative",
                display: "inline-block",
              }}
            >
              {t('navigation_menu.admin_dashboard')}
              {hasStatisticsAttention && (
                <div
                  style={{
                    position: "absolute",
                    top: "4px",
                    right: "-10px",
                    backgroundColor: "#d24e4e",
                    width: 8,
                    height: 8,
                    borderRadius: 10,
                  }}
                />
              )}
            </div>
          )}
        />
      )}

      {!usePreviewNav && (
        <MenuNavLink label={t('navigation_menu.events')} to={routes.EVENTS} icon={IconCalendar} />
      )}

      {!usePreviewNav && (
        <MenuNavLink label={t('navigation_menu.feed')} to={routes.FEED} icon={IconNotification} />
      )}

      <MenuNavLink 
        to={routes.MESSAGES} 
        icon={IconMessage} 
        menuItemChildren={
          <div 
            style={{
              position: "relative",
              display: "inline-block",
            }}
          >
            {t('navigation_menu.messages')}
            {unreadMessagesCount > 0 && (
              <div style={{
                position: "absolute",
                top: '5px',
                right: '-10px',
                backgroundColor: '#d24e4e',
                width: 8,
                height: 8,
                borderRadius: 10,
              }} />
            )}
          </div>
        }
      />
      <MenuNavLink to="" label={t('navigation_menu.log_out')} icon={IconPoweroff} onClick={logOut} />
    </>
  );

  return (
    <div style={{ width: "100%" }} className={classNames("own-header", {
      "own-header--black-theme": getCurrentThemeName() === "black",
    })}>
      <Row
        style={{
          paddingTop: 5,
          paddingBottom: 5,
          flexWrap: 'nowrap',
        }}
        justify="end"
        align="center"
      >
        <Col style={{ paddingTop: 2, minWidth: 0 }} flex="auto">
          <Row
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flexWrap: 'nowrap',
              minWidth: 0,
              justifyContent: 'flex-start',
            }}
          >
            <NavLink to={defaultPage} className={"own-header-logo"} style={{ flexShrink: 1, minWidth: 0 }}>
              <Logo
                size={133}
                variant="header"
                type={
                  getCurrentThemeName() === "black"
                    ? "women-nyc-menu"
                    : getAuthLogoType() === "tenant-brand"
                      ? "tenant-brand"
                      : "main"
                }
              />
            </NavLink>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <LanguageSelect withUserUpdate color={getCurrentTheme().headerTextColor}/>
              {user?.id && qrProfileEnabled ? (
                <button
                  type="button"
                  aria-label="Scan QR code"
                  onClick={() => setScannerOpen(true)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: getCurrentTheme().headerTextColor,
                    fontSize: 22,
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 4,
                    flexShrink: 0,
                  }}
                >
                  <IconQrcode />
                </button>
              ) : null}
            </div>
          </Row>
        </Col>
        <Col
          flex="none"
          className={"display-only-on-desktop"}
          style={{ maxWidth: "100%" }}
        >
          <Menu
            style={{
              backgroundColor: getCurrentTheme().headerColor,
              marginRight: 25,
            }}
            collapse={false}
            mode="horizontal"
            selectedKeys={[location.pathname]}
          >
            {menuItems}
          </Menu>
        </Col>
        <Col
          flex="none"
          style={{ textAlign: "right" }}
          className={"display-only-on-mobile"}
        >
          {/*{user?.userID && (*/}
          {/*  <Avatar size={40} onClick={() => setCollapse(!collapse)}>*/}
          {/*    <img src={user?.avatarUrl} alt="avatar" style={{width: "100%"}}/>*/}
          {/*  </Avatar>*/}
          {/*)}*/}
          <div style={{
            position: "relative",
            display: "inline-block",
          }}>
            <IconMenu
              onClick={() => setCollapse(!collapse)}
              style={{
                cursor: "pointer",
                float: "right",
                fontSize: 35,
                marginLeft: 10,
                marginRight: 10,
                marginTop: 3,
                color: getCurrentTheme().headerTextColor 
              }}
            />
            {unreadMessagesCount > 0 && (
              <div style={{
                position: "absolute",
                top: '3px',
                right: '5px',
                backgroundColor: '#d24e4e',
                width: 10,
                height: 10,
                borderRadius: 10,
              }}/>
            )}
          </div>
          {collapse && (
            <Menu
              style={{
                position: "absolute",
                zIndex: 999,
                backgroundColor: getCurrentTheme().headerColor,
                top: "43px",
                right: "5px",
                textAlign: "left",
                width: "100vw",
              }}
              collapse={false}
              mode="vertical"
              onClick={() => {
                setTimeout(() => {
                  setCollapse(false);
                }, 80);
              }}
            >
              {menuItems}
            </Menu>
          )}
        </Col>
      </Row>
      <QrScannerModal visible={scannerOpen} onClose={() => setScannerOpen(false)} />
    </div>
  );
};
